import { useEffect, useState } from "react";
import api from "../lib/api";
import { Calendar, Users, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function EventBookingModal({ event, onClose }) {
  const [form, setForm] = useState({ full_name: "", phone: "", guests: 2, notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/events/${event.id}/book`, { ...form, event_id: event.id, guests: Number(form.guests) });
      setSuccess(true);
      toast.success("Booking sent");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not book");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="event-booking-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-2xl text-[#5979A9]">Book event</h3>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        {success ? (
          <div className="text-center py-8">
            <CheckCircle2 size={56} className="text-[#E05947] mx-auto mb-4"/>
            <p className="text-[#4A5568]">Booking request sent for <strong>{event.name}</strong>. We'll confirm shortly.</p>
            <button onClick={onClose} className="btn-secondary mt-6">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-sm text-[#4A5568] bg-[#FAF4F1] rounded-xl p-3">
              <strong>{event.name}</strong> · {event.date} · {event.start}–{event.end}
            </div>
            <Input label="Full Name" required value={form.full_name} onChange={(v) => setForm({...form, full_name: v})} testid="event-name"/>
            <Input label="Phone" required value={form.phone} onChange={(v) => setForm({...form, phone: v})} testid="event-phone"/>
            <Input label="Guests" required type="number" min="1" max={event.remaining_capacity} value={form.guests} onChange={(v) => setForm({...form, guests: v})} testid="event-guests"/>
            <Input label="Notes (optional)" value={form.notes} onChange={(v) => setForm({...form, notes: v})} testid="event-notes"/>
            <button type="submit" disabled={submitting} data-testid="event-book-submit" className="btn-primary w-full">
              {submitting ? "Sending…" : "Send Booking Request"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-1.5 font-semibold">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2 text-[#2D3748]" {...rest}/>
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, []);

  if (events.length === 0) return null;

  return (
    <section id="events" data-testid="events-section" className="py-24 sm:py-28" style={{ background: "linear-gradient(180deg, #F0EAE6 0%, #FAF4F1 100%)" }}>
      <div className="container-wide">
        <div className="reveal max-w-3xl mb-12">
          <div className="eyebrow mb-3">Upcoming at the Cafe</div>
          <h2 className="section-title text-4xl sm:text-5xl">Events you'll love</h2>
          <p className="mt-4 text-[#4A5568] text-lg leading-relaxed">Live music, supper clubs, themed nights and seasonal celebrations. Reserve your seat.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((ev) => {
            const sold = ev.remaining_capacity <= 0;
            const limited = ev.remaining_capacity > 0 && ev.remaining_capacity <= Math.max(5, Math.floor(ev.capacity * 0.15));
            return (
              <article key={ev.id} data-testid={`event-card-${ev.id}`} className="bg-white rounded-2xl overflow-hidden border border-[#F0EAE6] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
                {ev.image_url && (
                  <div className="h-44 overflow-hidden">
                    <img src={ev.image_url} alt={ev.name} className="w-full h-full object-cover"/>
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#5979A9]/80 font-semibold"><Calendar size={12}/> {new Date(ev.date + "T00:00:00").toLocaleDateString(undefined, {weekday:'short', day:'numeric', month:'short'})}</span>
                    {ev.is_paid ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#5979A9]/10 text-[#5979A9] font-semibold">{ev.price || "Paid"}</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">Free</span>}
                  </div>
                  <h3 className="font-serif-display text-2xl text-[#5979A9] mb-2 leading-tight">{ev.name}</h3>
                  <p className="text-sm text-[#4A5568] mb-4 line-clamp-3 flex-1">{ev.description}</p>
                  <div className="text-sm text-[#4A5568] mb-4 flex items-center gap-3">
                    <span>{ev.start}–{ev.end}</span>
                    <span className="text-[#5979A9]/40">·</span>
                    <span className="inline-flex items-center gap-1"><Users size={14}/>
                      {sold ? <span className="text-[#E05947] font-semibold">Fully booked</span>
                        : limited ? <span className="text-yellow-700 font-semibold">{ev.remaining_capacity} seats left</span>
                        : <span className="text-green-700 font-semibold">Seats available</span>}
                    </span>
                  </div>
                  <button onClick={() => !sold && setActive(ev)} disabled={sold}
                          data-testid={`event-book-${ev.id}`}
                          className={`mt-auto self-start ${sold ? "px-5 py-2 rounded-full bg-gray-100 text-gray-400 text-sm cursor-not-allowed" : "btn-primary !py-2.5 !px-5 !text-sm"}`}>
                    {sold ? "Sold out" : "Book seat"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
      {active && <EventBookingModal event={active} onClose={() => setActive(null)}/>}
    </section>
  );
}
